import React, { useState } from 'react';
import { Pressable, StyleSheet, View, type DimensionValue, type StyleProp, type ViewStyle } from 'react-native';

import { Text } from '@/components/ui';

export interface LineItemColumn {
	key: string;
	label: string;
	/** Column width — `'30%'` for percentage (aligns row-to-row), `140` for fixed px. */
	width: DimensionValue;
	align?: 'left' | 'right';
}

export interface LineItemsTableProps<T> {
	columns: LineItemColumn[];
	lines: T[];
	renderCell: (args: { line: T; column: LineItemColumn; index: number }) => React.ReactNode;
	/** Per-column footer content (e.g. totals row). Return `null` to leave a column blank. */
	renderFooterCell?: (args: { column: LineItemColumn }) => React.ReactNode;
	/** Optional per-row style (e.g. highlight non-exact / new-record rows). */
	rowStyle?: (args: { line: T; index: number }) => StyleProp<ViewStyle>;
	/** When true, hovering a row outlines it (web) so it's easy to see the active row. */
	hoverable?: boolean;
}

/**
 * A row that outlines itself on hover (web). Shared so callers that render their
 * own rich rows (not just table cells) get the same highlight without copying
 * the hover state + outline style. `hoverable=false` renders a plain View.
 */
export const HoverRow = ({ hoverable, style, testID, children }: {
	hoverable?: boolean;
	style: StyleProp<ViewStyle>;
	testID?: string;
	children: React.ReactNode;
}): React.ReactElement => {
	const [hovered, setHovered] = useState(false);
	if (!hoverable) return <View style={style} testID={testID}>{children}</View>;
	return (
		<Pressable
			testID={testID}
			style={[style, hovered && styles.rowHover]}
			onHoverIn={() => setHovered(true)}
			onHoverOut={() => setHovered(false)}
		>
			{children}
		</Pressable>
	);
};

const BORDER = '#cfd8dc';

const colStyle = (col: LineItemColumn): ViewStyle => ({
	width: col.width,
	flexGrow: 0,
	flexShrink: 0,
	flexBasis: col.width ?? undefined,
});

/**
 * Excel-style bordered grid for line-item composers (invoices, journal entries).
 * Cells supply their own bare inputs (no rounded outlines) — the grid provides
 * the borders so columns align row-to-row. Callers own line-state and should
 * use a trailing-blank reconciliation helper so the last row is always empty,
 * spawning a new one as soon as the user types into it.
 */
export const LineItemsTable = <T,>({
	columns,
	lines,
	renderCell,
	renderFooterCell,
	rowStyle,
	hoverable,
}: LineItemsTableProps<T>): React.ReactElement => {
	const lastColIdx = columns.length - 1;
	return (
		<View style={styles.grid}>
			<View style={[styles.row, styles.headerRow]}>
				{columns.map((col, ci) => (
					<View key={col.key} style={[styles.cell, colStyle(col), ci === lastColIdx && styles.colLast]}>
						<Text style={[styles.headerLabel, col.align === 'right' && styles.right]}>{col.label}</Text>
					</View>
				))}
			</View>
			{lines.map((line, i) => (
				<HoverRow key={i} hoverable={hoverable} style={[styles.row, rowStyle?.({ line, index: i })]}>
					{columns.map((col, ci) => (
						<View
							key={col.key}
							style={[styles.cell, colStyle(col), ci === lastColIdx && styles.colLast]}
						>
							{renderCell({ line, column: col, index: i })}
						</View>
					))}
				</HoverRow>
			))}
			{renderFooterCell && (
				<View style={[styles.row, styles.footerRow]}>
					{columns.map((col, ci) => (
						<View
							key={col.key}
							style={[styles.cell, colStyle(col), ci === lastColIdx && styles.colLast, styles.footerCell]}
						>
							{renderFooterCell({ column: col })}
						</View>
					))}
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	grid: { borderWidth: 1, borderColor: BORDER, backgroundColor: '#fff' },
	row: {
		flexDirection: 'row', alignItems: 'stretch',
		borderBottomWidth: 1, borderBottomColor: BORDER,
		minHeight: 32,
	},
	rowHover: { outlineWidth: 2, outlineColor: '#1e88e5', outlineStyle: 'solid' } as ViewStyle,
	headerRow: { backgroundColor: '#fafbfc' },
	footerRow: { backgroundColor: '#fafbfc', borderBottomWidth: 0 },
	footerCell: { paddingVertical: 8, paddingHorizontal: 8 },
	cell: {
		borderRightWidth: 1, borderRightColor: BORDER,
		justifyContent: 'center',
		minHeight: 32,
		// minWidth: 0 (with flexBasis: 0) forces RN-Web to obey width:%. Without it,
		// TextInput intrinsic width beats flex-basis and rows misalign.
		minWidth: 0,
		overflow: 'visible',
	},
	colLast: { borderRightWidth: 0 },
	headerLabel: {
		fontSize: 10, fontWeight: '700',
		color: '#546e7a',
		textTransform: 'uppercase', letterSpacing: 0.5,
		paddingHorizontal: 8, paddingVertical: 6,
	},
	right: { textAlign: 'right' },
});
